import { Tabs, type TabItem } from '@amelio/ui-library';
import { useTranslation } from 'react-i18next';

import type { PillarScopeType } from '@/constants/PillarScope';
import type { FilteredTeam } from '@/hooks/objectives/useObjectiveListLens';
import type { usePillarScope } from '@/hooks/objectives/usePillarScope';

import { PLACEHOLDER_BONE_COLOR } from '@/components/objectives/objectiveStyles';
import PillarScopeMention from '@/components/objectives/PillarScopeMention';
import { Bone, Skeleton } from '@/components/Skeleton/Skeleton';
import { PillarScope } from '@/constants/PillarScope';

interface PillarScopeBarProps {
    /**
     * The scope in effect, the scopes offered and the way to pick another, as the page's lens reads
     * them from the address. While the scope asked for is still being resolved, the bar holds its
     * place rather than naming a scope it may not turn out to be.
     */
    pillarScope: Pick<
        ReturnType<typeof usePillarScope>,
        'scope' | 'isTeamOffered' | 'isFiltersOffered' | 'setScope' | 'isResolvingScope'
    >;
    /** What the sentence under the bar speaks of: the row of pillars, or the pillar of its page. */
    subject: 'pillars' | 'pillar';
    /** The filters keep drafts alone, which count in no pillar: the bar says so under the sentence. */
    keepsDraftsOnly?: boolean;
    /**
     * The people of the reader's team the filters keep: under "selected filters", the sentence
     * counts them and the count opens their list. Absent for a reader who sees the whole company,
     * who reads the sentence bare.
     */
    filteredTeam?: FilteredTeam;
}

/** The room the bar keeps below itself, loaded or still resolving, before what it scopes. */
const BAR_BOTTOM_SPACE = 'mb-5';

/**
 * Whose objectives the pillars are counted among -- the whole organization, the filters picked
 * below, the reader's direct team -- and a sentence saying which, shared by the objectives page and a
 * pillar's page. The choice outranks the filters: they narrow the list below whatever is picked
 * here, and reach the pillars under "selected filters" alone.
 */
export default function PillarScopeBar({
    pillarScope,
    subject,
    keepsDraftsOnly = false,
    filteredTeam,
}: PillarScopeBarProps) {
    const { t } = useTranslation();
    const { scope, isTeamOffered, isFiltersOffered, setScope } = pillarScope;

    if (pillarScope.isResolvingScope) {
        return (
            <Skeleton className={`${BAR_BOTTOM_SPACE} flex justify-center`}>
                <div
                    role={'status'}
                    aria-busy
                    aria-label={t('objectives.pillarScope.resolving')}
                >
                    <Bone
                        color={PLACEHOLDER_BONE_COLOR}
                        className={'h-10 w-64 rounded-xl'}
                    />
                </div>
            </Skeleton>
        );
    }

    const items: TabItem[] = [
        { key: PillarScope.ORGANIZATION, label: t('objectives.pillarScope.organization') },
        ...(isFiltersOffered
            ? [{ key: PillarScope.FILTERS, label: t('objectives.pillarScope.filters') }]
            : []),
        ...(isTeamOffered
            ? [{ key: PillarScope.TEAM, label: t('objectives.pillarScope.team') }]
            : []),
    ];

    return (
        <div
            className={`${BAR_BOTTOM_SPACE} text-obj-slate flex flex-col items-center gap-2.5 text-center text-xs`}
            data-testid={'pillar-scope-bar'}
        >
            <Tabs
                variant={'capsule'}
                items={items}
                activeKey={scope}
                onChange={(key) => setScope(key as PillarScopeType)}
                ariaLabel={t('objectives.pillarScope.label')}
            />

            <PillarScopeMention
                scope={scope}
                subject={subject}
                filteredTeam={filteredTeam}
            />

            {keepsDraftsOnly && scope === PillarScope.FILTERS && (
                <p>{t('objectives.pillarScope.draftsCountNowhere')}</p>
            )}
        </div>
    );
}
